import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toUpper } from "lodash";
import { AuxNotificationStyleEnum, AuxNotificationToastTypeEnum } from "@blk/aladdin-web-components";
import { getConfig } from "plugins/config-loader";
import { addNotification } from "store/notification";
import { language } from "language/default";
import { buildDocumentEndpoints } from "./documents/api";
const dispatchNotesSaveToast = (dispatch, message, notificationStyle) => {
    dispatch(addNotification({
        id: Date.now().toString(),
        message,
        notificationStyle,
        showMessageCount: false,
        toastTimeout: 1000,
        toastType: AuxNotificationToastTypeEnum.TIMEOUT
    }));
};
const getNotesSaveToastMessages = (request) => {
    const isCreate = request.notes.some((note) => !note.id);
    return {
        error: isCreate
            ? language.en.notes.saveNotificationMessage.createFailed
            : language.en.notes.saveNotificationMessage.editFailed,
        pending: language.en.notes.saveNotificationMessage.pending,
        success: isCreate
            ? language.en.notes.saveNotificationMessage.createSuccess
            : language.en.notes.saveNotificationMessage.editSuccess
    };
};
const onBulkCreateUpdateNotesStarted = async (request, api) => {
    const messages = getNotesSaveToastMessages(request);
    dispatchNotesSaveToast(api.dispatch, messages.pending, AuxNotificationStyleEnum.MESSAGE);
    try {
        await api.queryFulfilled;
        dispatchNotesSaveToast(api.dispatch, messages.success, AuxNotificationStyleEnum.SUCCESS);
    }
    catch {
        dispatchNotesSaveToast(api.dispatch, messages.error, AuxNotificationStyleEnum.ERROR);
    }
};
const buildEndpoints = (builder) => ({
    bulkCreateUpdateNotes: builder.mutation({
        onQueryStarted: (request, api) => onBulkCreateUpdateNotesStarted(request, api),
        query: (request) => ({
            body: request,
            method: "POST",
            url: "/notes/bulk-create-update"
        })
    }),
    bulkDeleteNotes: builder.mutation({
        invalidatesTags: ["Notes"],
        query: (request) => ({
            body: request,
            method: "POST",
            url: "/notes/bulk-delete"
        })
    }),
    createCategory: builder.mutation({
        invalidatesTags: ["Categories"],
        query: (request) => ({
            body: request,
            method: "POST",
            url: "/categories"
        })
    }),
    deleteCategory: builder.mutation({
        invalidatesTags: ["Categories"],
        query: (request) => ({
            method: "DELETE",
            url: `/categories/${request.id}`
        })
    }),
    ...buildDocumentEndpoints(builder),
    getCategories: builder.query({
        providesTags: ["Categories"],
        query: () => ({
            method: "GET",
            url: "/categories"
        })
    }),
    getNoteById: builder.query({
        providesTags: (result, error, arg) => [{ id: arg.id, type: "Notes" }],
        query: (request) => ({
            method: "GET",
            url: `/notes/${request.id}`
        })
    }),
    getNotes: builder.query({
        providesTags: ["Notes"],
        query: (request) => ({
            method: "GET",
            params: {
                additionalEntityId: request.additionalEntityId,
                entityId: request.entityId,
                entityType: toUpper(request.entityType)
            },
            url: "/notes"
        })
    }),
    getNotesById: builder.query({
        providesTags: ["Notes"],
        query: (id) => ({
            method: "GET",
            url: `/notes/${id}`
        })
    }),
    getNotesList: builder.query({
        providesTags: ["Notes"],
        query: (request) => ({
            method: "GET",
            params: request,
            url: "/notes/list"
        })
    }),
    updateCategory: builder.mutation({
        invalidatesTags: ["Categories"],
        query: (request) => ({
            body: { name: request.name },
            method: "PUT",
            url: `/categories/${request.id}`
        })
    })
});
export const noteModuleName = "notes";
export const notesApiReducerPath = "notesApi";
export const notesApi = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: `${getConfig("REACT_APP_NOTES_BACKEND_BASE_URL")}`,
        prepareHeaders: (headers, { getState, extra }) => {
            const state = getState();
            const token = extra.getStateValue(state, "accessToken");
            const connectionId = extra.getStateValue(state, "connectionId");
            if (token) {
                headers.set("authorization", `Bearer ${token}`);
            }
            if (connectionId) {
                headers.set("Connection-Id", connectionId);
            }
            return headers;
        }
    }),
    endpoints: buildEndpoints,
    reducerPath: notesApiReducerPath,
    tagTypes: ["Notes", "Categories"]
});
export const { middleware: notesApiMiddleware, reducer: notesApiReducer, useGetCategoriesQuery, useGetNotesListQuery, useLazyGetNotesListQuery, useLazyGetCategoriesQuery, useGetNotesQuery, useLazyGetNotesQuery, useGetNoteByIdQuery, useLazyGetNoteByIdQuery, useBulkCreateUpdateNotesMutation, useBulkDeleteNotesMutation, useCreateCategoryMutation, useUpdateCategoryMutation, useDeleteCategoryMutation, useUploadDocumentMutation, useDeleteDocumentMutation } = notesApi;
export const notesFetched = notesApi.endpoints.getNotes.matchFulfilled;
export const notesListFetched = notesApi.endpoints.getNotesList.matchFulfilled;
export const notesCreatedOrUpdated = notesApi.endpoints.bulkCreateUpdateNotes.matchFulfilled;
export const notesDeleted = notesApi.endpoints.bulkDeleteNotes.matchFulfilled;
export const notesSavedFailed = notesApi.endpoints.bulkCreateUpdateNotes.matchRejected;
export const isFetchNotesAction = (action) => [
    notesFetched,
    notesListFetched
].some((matcher) => matcher(action));
